import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { AppConfig, environment } from '../../../environments/environment';
import { supabase } from '../../core/supabase/supabase.client';
import { AuthService } from '../../services/auth/auth.service';
import { SnackbarService } from '../../services/design/snackbar.service';

@Component({
  selector: 'app-admin-auth',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './auth.component.html',
})
export class AdminAuthComponent {
  readonly #router = inject(Router);
  readonly #snackbar = inject(SnackbarService);
  readonly #auth = inject(AuthService);

  appConfig: AppConfig = environment.appConfig;

  email = '';
  password = '';
  showPassword = false;
  submitting = false;
  recoveryMode = false;
  recoverySubmitting = false;

  async login() {
    if (!this.email || !this.password) {
      this.#snackbar.error('Enter email and password');
      return;
    }

    this.submitting = true;
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: this.email.trim(),
        password: this.password,
      });

      if (error) {
        this.#snackbar.error(error.message);
        return;
      }

      const userId = data.user?.id;
      if (!userId) {
        this.#snackbar.error('Login failed');
        return;
      }

      try {
        const ok = await this.#auth.loadUser(userId);
        if (!ok) {
          this.#snackbar.error('Access denied');
          return;
        }
      } catch (error: unknown) {
        this.#snackbar.error(
          error instanceof Error
            ? error.message
            : 'This account cannot access the admin panel.'
        );
        return;
      }

      await this.#router.navigate([this.#auth.homePath()]);
    } finally {
      this.submitting = false;
    }
  }

  showPasswordRecovery() {
    this.recoveryMode = true;
  }

  showLogin() {
    this.recoveryMode = false;
  }

  async requestPasswordReset() {
    const email = this.email.trim();
    if (!this.isValidEmail(email)) {
      this.#snackbar.error('Enter a valid email address');
      return;
    }

    this.recoverySubmitting = true;
    try {
      await this.#auth.resetPassword(email);
      this.#snackbar.success(
        'If an account exists for this email, password reset instructions have been sent.'
      );
      this.showLogin();
    } catch {
      this.#snackbar.error(
        'Unable to send password reset instructions. Please try again shortly.'
      );
    } finally {
      this.recoverySubmitting = false;
    }
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
}
